// Shared building blocks for the Accident Metrics dashboard family.
import { useState } from "react";
import { Filter, X, TrendingUp, TrendingDown, Minus } from "lucide-react";

export const PALETTE = ["#C75A1B", "#2A5DB0", "#C68A1C", "#3F7D3A", "#B33A2E", "#6B4E9E", "#9A968F", "#D9772E", "#4C9A8A", "#8A6D3B"];
export const CATEGORY_COLORS = {
    "No Treatment": "#9A968F", "First Aid": "#3F7D3A", "Medical Aid": "#C68A1C",
    "Lost Time": "#D9772E", "Lost Time RIDDOR": "#B33A2E", "Fatality": "#1F1F1F",
};
export const TT = { fontSize: 12, borderRadius: 8, border: "1px solid #E5E0DA", boxShadow: "0 4px 14px rgba(0,0,0,.08)" };

export const EMPTY_FILTERS = {
    client_id: "", building_id: "", project_id: "", year: "", month: "", months: 12,
    incident_type: "", severity: "", riddor: "", accident_category: "", shift: "",
    department: "", location: "", initial_cause: "", root_cause_category: "", unsafe_classification: "",
};

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

export function buildParams(f) {
    const p = {};
    if (f.client_id) p.client_id = f.client_id;
    if (f.building_id) p.building_id = f.building_id;
    if (f.project_id) p.project_id = f.project_id;
    if (f.year) p.year = f.year;
    if (f.year && f.month) p.month = f.month;
    if (!f.year) p.months = f.months || 12;
    for (const k of ["incident_type", "severity", "riddor", "accident_category", "shift",
        "department", "location", "initial_cause", "root_cause_category", "unsafe_classification"]) {
        if (f[k]) p[k] = f[k];
    }
    return p;
}

// Convert dashboard time filters into list-endpoint date_from/date_to (for drilldowns).
export function dateRangeFromFilters(f) {
    const out = {};
    if (f.year && f.month) {
        out.date_from = `${f.year}-${String(f.month).padStart(2, "0")}-01`;
        const last = new Date(f.year, f.month, 0).getDate();
        out.date_to = `${f.year}-${String(f.month).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
    } else if (f.year) {
        out.date_from = `${f.year}-01-01`;
        out.date_to = `${f.year}-12-31`;
    } else {
        const d = new Date();
        d.setMonth(d.getMonth() - ((f.months || 12) - 1));
        d.setDate(1);
        out.date_from = d.toISOString().slice(0, 10);
    }
    return out;
}

const selCls = "h-9 px-2.5 rounded-lg border border-[#E5E0DA] bg-white text-[13px] focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)]";

function Sel({ value, onChange, children, testid, disabled }) {
    return (
        <select data-testid={testid} value={value} disabled={disabled}
            onChange={(e) => onChange(e.target.value)} className={selCls + (disabled ? " bg-[#F6F4F0]" : "")}>
            {children}
        </select>
    );
}

export function FilterBar({ filters, onChange, clients = [], buildings = [], projects = [], meta = {}, isPortal = false }) {
    const [more, setMore] = useState(false);
    const set = (k, v) => onChange({ ...filters, [k]: v });
    const yearNow = new Date().getFullYear();
    const years = [yearNow, yearNow - 1, yearNow - 2];
    const buildingsForClient = buildings.filter((b) => !filters.client_id || b.client_id === filters.client_id);
    const projectsForClient = projects.filter((p) => !filters.client_id || p.client_id === filters.client_id);

    const advancedKeys = ["incident_type", "severity", "riddor", "accident_category", "shift",
        "department", "location", "initial_cause", "root_cause_category", "unsafe_classification", "project_id", "building_id"];
    const activeCount = advancedKeys.filter((k) => filters[k]).length;
    const anyActive = activeCount > 0 || filters.year || (filters.months && filters.months !== 12);

    return (
        <div data-testid="metrics-filter-bar" className="bg-white border border-[#E5E0DA] rounded-[11px] p-3.5 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
                <Sel testid="filter-client" value={filters.client_id} disabled={isPortal}
                    onChange={(v) => onChange({ ...filters, client_id: v, building_id: "", project_id: "" })}>
                    <option value="">All clients</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Sel>
                <Sel testid="filter-building" value={filters.building_id} onChange={(v) => set("building_id", v)}>
                    <option value="">All buildings</option>
                    {buildingsForClient.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </Sel>
                <Sel testid="filter-year" value={filters.year} onChange={(v) => onChange({ ...filters, year: v, month: "" })}>
                    <option value="">Rolling window</option>
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </Sel>
                {filters.year ? (
                    <Sel testid="filter-month" value={filters.month} onChange={(v) => set("month", v)}>
                        <option value="">Whole year</option>
                        {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </Sel>
                ) : (
                    <Sel testid="filter-months" value={String(filters.months || 12)} onChange={(v) => set("months", parseInt(v, 10))}>
                        <option value="6">Last 6 months</option>
                        <option value="12">Last 12 months</option>
                        <option value="24">Last 24 months</option>
                    </Sel>
                )}
                <button data-testid="filter-more-toggle" onClick={() => setMore((m) => !m)}
                    className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-[13px] font-semibold transition ${more ? "border-[var(--brand-accent,#C75A1B)] text-[#C75A1B]" : "border-[#E5E0DA] text-[#6F6A64] hover:bg-[#F6F4F0]"}`}>
                    <Filter size={13} /> More filters{activeCount ? ` · ${activeCount}` : ""}
                </button>
                {anyActive && (
                    <button data-testid="filter-clear" onClick={() => onChange({ ...EMPTY_FILTERS, client_id: isPortal ? filters.client_id : "" })}
                        className="inline-flex items-center gap-1 h-9 px-3 rounded-lg text-[13px] text-[#6F6A64] hover:text-[#B33A2E]">
                        <X size={13} /> Clear
                    </button>
                )}
            </div>
            {more && (
                <div data-testid="filter-more-panel" className="grid sm:grid-cols-3 lg:grid-cols-4 gap-2.5 pt-1 border-t border-[#F0EDE8]">
                    <Sel testid="filter-type" value={filters.incident_type} onChange={(v) => set("incident_type", v)}>
                        <option value="">Any type</option>
                        {(meta.types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                    </Sel>
                    <Sel testid="filter-severity" value={filters.severity} onChange={(v) => set("severity", v)}>
                        <option value="">Any severity</option>
                        {(meta.severities || []).map((s) => <option key={s} value={s}>{s}</option>)}
                    </Sel>
                    <Sel testid="filter-riddor" value={filters.riddor} onChange={(v) => set("riddor", v)}>
                        <option value="">RIDDOR: any</option>
                        <option value="yes">RIDDOR only</option>
                        <option value="no">Non-RIDDOR</option>
                    </Sel>
                    <Sel testid="filter-category" value={filters.accident_category} onChange={(v) => set("accident_category", v)}>
                        <option value="">Any category</option>
                        {(meta.accident_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </Sel>
                    <Sel testid="filter-shift" value={filters.shift} onChange={(v) => set("shift", v)}>
                        <option value="">Any shift</option>
                        {(meta.shifts || []).map((s) => <option key={s} value={s}>{s}</option>)}
                    </Sel>
                    <Sel testid="filter-department" value={filters.department} onChange={(v) => set("department", v)}>
                        <option value="">Any department</option>
                        {[...new Set(["Facilities", "Grounds & Cemeteries", "Library Services", "Administration", "Waste & Recycling", "Events"])].map((d) => <option key={d} value={d}>{d}</option>)}
                    </Sel>
                    <Sel testid="filter-initial-cause" value={filters.initial_cause} onChange={(v) => set("initial_cause", v)}>
                        <option value="">Any initial cause</option>
                        {(meta.initial_causes || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </Sel>
                    <Sel testid="filter-root-cause" value={filters.root_cause_category} onChange={(v) => set("root_cause_category", v)}>
                        <option value="">Any root cause</option>
                        {(meta.root_cause_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </Sel>
                    <Sel testid="filter-unsafe" value={filters.unsafe_classification} onChange={(v) => set("unsafe_classification", v)}>
                        <option value="">Act / condition: any</option>
                        {(meta.unsafe_classifications || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </Sel>
                    {projectsForClient.length > 0 && (
                        <Sel testid="filter-project" value={filters.project_id} onChange={(v) => set("project_id", v)}>
                            <option value="">Any project</option>
                            {projectsForClient.map((p) => <option key={p.id} value={p.id}>{p.name || p.reference}</option>)}
                        </Sel>
                    )}
                    <input data-testid="filter-location" placeholder="Location contains…" value={filters.location}
                        onChange={(e) => set("location", e.target.value)} className={selCls} />
                </div>
            )}
        </div>
    );
}

const TONES = {
    accent: { bg: "#FBEFE5", fg: "#9F4212" }, red: { bg: "#FBE8E5", fg: "#B33A2E" },
    amber: { bg: "#FCF1DC", fg: "#C68A1C" }, green: { bg: "#E8F1E6", fg: "#3F7D3A" },
    blue: { bg: "#EAF1FB", fg: "#2A5DB0" }, neutral: { bg: "#F0EDE8", fg: "#6F6A64" },
};

export function KpiCard({ testid, icon: Icon, label, value, tone = "neutral", sub, onClick }) {
    const tn = TONES[tone] || TONES.neutral;
    return (
        <div data-testid={testid} onClick={onClick}
            className={`bg-white border border-[#E5E0DA] rounded-[11px] p-3.5 ${onClick ? "cursor-pointer hover:border-[var(--brand-accent,#C75A1B)] transition" : ""}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="w-[30px] h-[30px] rounded-[8px] grid place-items-center" style={{ background: tn.bg, color: tn.fg }}>
                    {Icon && <Icon className="w-[15px] h-[15px]" />}
                </div>
            </div>
            <div className="font-display font-bold text-[24px] leading-none text-[#1F1F1F]">{value}</div>
            <div className="text-[11px] mt-1.5 font-medium text-[#6F6A64]">{label}</div>
            {sub && <div className="text-[10.5px] mt-0.5 text-[#9A968F]">{sub}</div>}
        </div>
    );
}

export function ChartCard({ testid, title, subtitle, children, right }) {
    return (
        <div data-testid={testid} className="bg-white border border-[#E5E0DA] rounded-[11px] p-5">
            <div className="mb-3 flex items-start justify-between gap-2">
                <div>
                    <h3 className="text-[13.5px] font-semibold text-[#1F1F1F]">{title}</h3>
                    {subtitle && <p className="text-[11px] text-[#6F6A64] mt-0.5">{subtitle}</p>}
                </div>
                {right}
            </div>
            {children}
        </div>
    );
}

export function Empty({ label = "No data for the current filters." }) {
    return <div className="text-[12px] italic text-[#9A968F] py-10 text-center">{label}</div>;
}

export { TrendingUp, TrendingDown, Minus };
